import {
  createWorkflowTemplateLink,
  useCommendaServices,
  useGetFilingTypes,
  useWorkflowTemplates,
} from '@commenda/api';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  Loading,
  SingleCombobox,
  useToggle,
} from '@commenda/kakashi';
import { TCreateWorkflowTemplateLinkParams } from '@commenda/types';
import { cn } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { WorkflowLinkType } from '@prisma/client';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNotification } from '../../../hooks/useNotification';
import { getServiceCatalogTypesForWorkflows } from '../../helpers/get-service-catalog-types-for-workflows';

const baseSchema = z.object({
  workflowTemplateId: z
    .string({ required_error: 'Required' })
    .min(1, 'Required'),
  serviceCompositeId: z.string().optional(),
  entityType: z.string().optional(),
  entityId: z.string().optional(),
});

type TCreateWorkflowTemplateLinkFormFields = z.infer<typeof baseSchema>;

type TCreateWorkflowTemplateLinkFormProps = {
  onSuccess: () => void;
  onCancel: () => void;
  formContentClassName?: string;
  formFooterClassName?: string;
  /** Pre-selects the workflow and hides the picker, for callers that already know which workflow this is. */
  lockedWorkflowTemplateId?: string;
};

export const CreateWorkflowTemplateLinkForm = ({
  onSuccess,
  onCancel,
  formContentClassName,
  formFooterClassName,
  lockedWorkflowTemplateId,
}: TCreateWorkflowTemplateLinkFormProps) => {
  const servicesEnabled = isFeatureLaunched(
    FeatureLaunch.COMMENDA_SERVICES_LAYER
  );
  const isSubmitting = useToggle(false);
  const { setNotification } = useNotification();
  const {
    data: workflowTemplates,
    error: workflowTemplatesError,
    isLoading: workflowTemplatesLoading,
  } = useWorkflowTemplates();
  const { data: filingTypesData } = useGetFilingTypes();
  const { data: commendaServicesData } = useCommendaServices({
    limit: 500,
    enabled: servicesEnabled,
  });

  const schema = baseSchema.superRefine((data, ctx) => {
    if (servicesEnabled) {
      if (!data.serviceCompositeId && !(data.entityType && data.entityId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['serviceCompositeId'],
          message: 'Select a service, or set an advanced link target',
        });
      }
      if (data.serviceCompositeId && (data.entityType || data.entityId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['serviceCompositeId'],
          message:
            'Choose either a service or an advanced link target, not both',
        });
      }
      if (data.entityType && !data.entityId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['entityId'],
          message: 'Required for the selected link type',
        });
      }
    } else {
      if (!data.entityType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['entityType'],
          message: 'Required',
        });
      }
      if (!data.entityId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['entityId'],
          message: 'Required',
        });
      }
    }
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<TCreateWorkflowTemplateLinkFormFields>({
    resolver: zodResolver(schema),
    defaultValues: lockedWorkflowTemplateId
      ? { workflowTemplateId: lockedWorkflowTemplateId }
      : undefined,
  });

  const setField = (
    field: keyof TCreateWorkflowTemplateLinkFormFields,
    value: string
  ) => {
    clearErrors();
    setValue(field, value);
  };

  const onSubmit = async (data: TCreateWorkflowTemplateLinkFormFields) => {
    isSubmitting.on();
    const payload: TCreateWorkflowTemplateLinkParams = {
      workflowTemplateId: data.workflowTemplateId,
      entityType: (data.entityType as WorkflowLinkType) || undefined,
      entityId: data.entityId || undefined,
      serviceCompositeId: servicesEnabled
        ? data.serviceCompositeId || undefined
        : undefined,
    };
    const response = await createWorkflowTemplateLink(payload);
    if (!response.success) {
      setNotification({
        title: 'Error',
        message: response.error ?? 'Failed to create workflow template link',
        show: true,
        valid: false,
      });
      isSubmitting.off();
      return;
    }
    setNotification({
      title: 'Success',
      message: 'Workflow template link created successfully',
      show: true,
      valid: true,
    });
    onSuccess();
    isSubmitting.off();
  };

  if (workflowTemplatesError || workflowTemplatesLoading) {
    return <Loading />;
  }

  const svcCatalogTypesForWorkflows = getServiceCatalogTypesForWorkflows();

  const legacyLinkFields = (
    <>
      <FieldWrapper>
        <Label>Link Type</Label>
        <SingleCombobox
          options={Object.values(WorkflowLinkType).map((each) => ({
            label: each,
            value: each,
          }))}
          value={watch('entityType')}
          setValue={(value) => setField('entityType', value)}
        />
        {errors.entityType?.message && (
          <InputErrorMessage message={errors.entityType.message} />
        )}
      </FieldWrapper>
      {watch('entityType') === WorkflowLinkType.SERVICE_CATALOG ? (
        <FieldWrapper>
          <Label>Service Catalog</Label>
          <SingleCombobox
            options={svcCatalogTypesForWorkflows.map((serviceCatalog) => ({
              label: serviceCatalog,
              value: serviceCatalog,
            }))}
            value={watch('entityId')}
            setValue={(value) => setField('entityId', value)}
          />
        </FieldWrapper>
      ) : watch('entityType') === WorkflowLinkType.COMPLIANCE_TYPE ? (
        <FieldWrapper>
          <Label>Compliance Type</Label>
          <SingleCombobox
            options={(filingTypesData?.filingTypes ?? []).map((filingType) => ({
              label: filingType.displayName,
              value: filingType.id,
            }))}
            value={watch('entityId')}
            setValue={(value) => setField('entityId', value)}
          />
        </FieldWrapper>
      ) : (
        <FieldWrapper>
          <Label>Product Id</Label>
          <Input {...register('entityId')} />
        </FieldWrapper>
      )}
      {errors.entityId?.message && (
        <InputErrorMessage message={errors.entityId.message} />
      )}
    </>
  );

  return (
    <div>
      <div className={cn('flex flex-col gap-4', formContentClassName)}>
        {!lockedWorkflowTemplateId && (
          <FieldWrapper>
            <Label>Workflow template</Label>
            <SingleCombobox
              options={workflowTemplates.workflowTemplates.map(
                (workflowTemplate) => ({
                  label: workflowTemplate.name,
                  value: workflowTemplate.id,
                })
              )}
              value={watch('workflowTemplateId')}
              setValue={(value) => setField('workflowTemplateId', value)}
            />
            {errors.workflowTemplateId?.message && (
              <InputErrorMessage message={errors.workflowTemplateId.message} />
            )}
          </FieldWrapper>
        )}
        {servicesEnabled ? (
          <>
            <FieldWrapper>
              <Label>Service</Label>
              <SingleCombobox
                options={(commendaServicesData?.items ?? []).map((service) => ({
                  label: `${service.title} (${service.serviceCompositeId})`,
                  value: service.serviceCompositeId,
                }))}
                value={watch('serviceCompositeId')}
                setValue={(value) => setField('serviceCompositeId', value)}
              />
              {errors.serviceCompositeId?.message && (
                <InputErrorMessage
                  message={errors.serviceCompositeId.message}
                />
              )}
            </FieldWrapper>
            <Accordion type="single" collapsible>
              <AccordionItem
                value="advanced"
                className="border-b-0 bg-transparent"
              >
                <AccordionTrigger className="px-0 py-2 text-sm text-text-secondary">
                  Advanced link settings
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4 pt-2">
                  {legacyLinkFields}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </>
        ) : (
          legacyLinkFields
        )}
      </div>
      <div className={cn('flex gap-2 justify-end', formFooterClassName)}>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(onSubmit)}>Add Link</Button>
      </div>
    </div>
  );
};
